import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { constants } from '../../../constants';
import { AgeGroup } from '../models/age-group.model';
import { DashboardFilter } from '../models/custom-dashboard.model';
import { RangeFilter } from '../models/custom-filter-range.model';
import { RegionFilter } from '../models/region-group.model';
import { SpendingGroup } from '../models/spending-group.model';
import { StatisticsRequest } from '../models/statistics-request.model';
import { catchError, finalize, tap } from 'rxjs/operators';
import {
  CitySearch,
  SelectorCity,
  SelectorCountry,
} from '../models/region-selection-cities.model';
import { GlobalErrorModalService } from './global-error-modal.service';
import { CityRegion, Region } from '../models/region-model';
import { IncomeGroup } from '../models/income-group.model';
import { ExportRequest } from '../models/exported-data.model';

@Injectable({
  providedIn: 'root',
})
export class DashboardStatisticsService {
  public cityCountries = new Map<string, SelectorCountry[]>();
  private customAgeGroups: BehaviorSubject<AgeGroup[]> = new BehaviorSubject<
    AgeGroup[]
  >([]);
  private customSpendingGroups: BehaviorSubject<SpendingGroup[]> =
    new BehaviorSubject<SpendingGroup[]>([]);
  private customIncomeGroups: BehaviorSubject<IncomeGroup[]> =
    new BehaviorSubject<IncomeGroup[]>([]);

  constructor(
    private http: HttpClient,
    private globalErrorModalService: GlobalErrorModalService
  ) {}

  public setCustomAgeGroups(customAgeGroups: AgeGroup[]): void {
    this.customAgeGroups.next(customAgeGroups);
  }

  public getCustomAgeGroups(): Observable<AgeGroup[]> {
    return this.customAgeGroups.asObservable();
  }

  public setCustomSpendingGroups(customSpendingGroups: SpendingGroup[]): void {
    this.customSpendingGroups.next(customSpendingGroups);
  }

  public setCustomIncomeGroups(customIncomeGroups: IncomeGroup[]): void {
    this.customIncomeGroups.next(customIncomeGroups);
  }

  public getCustomSpendingGroups(): Observable<SpendingGroup[]> {
    return this.customSpendingGroups.asObservable();
  }

  public getCustomIncomeGroups(): Observable<IncomeGroup[]> {
    return this.customIncomeGroups.asObservable();
  }

  public loadRangeCustomFilters() {
    return this.http.get<RangeFilter[]>(constants.uriParts.customRangeFilters);
  }

  public saveRangeCustomFilter(rangeFilter: RangeFilter): Observable<Object> {
    return this.http.post(constants.uriParts.customRangeFilters, rangeFilter);
  }

  public updateRangeCustomFilter(rangeFilter: RangeFilter): Observable<Object> {
    return this.http.put(
      constants.uriParts.customRangeFilters + '/' + rangeFilter.id?.toString(),
      rangeFilter
    );
  }

  public deleteRangeCustomFilter(id: number): Observable<Object> {
    return this.http.delete(
      constants.uriParts.customRangeFilters + '/' + id.toString()
    );
  }

  public saveRegionCustomFilter(regionGroup: RegionFilter) {
    return this.http.post(constants.uriParts.customRegionFilters, regionGroup);
  }

  public loadRegionCustomFilters() {
    return this.http.get<RegionFilter[]>(
      constants.uriParts.customRegionFilters
    );
  }

  public deleteRegionCustomFilter(id: number) {
    return this.http.delete(
      constants.uriParts.customRegionFilters + '/' + id.toString()
    );
  }

  public loadExportSaveFile(dataRequest: ExportRequest, fileFormat: string) {
    let bearerToken = localStorage.currentUser;
    bearerToken = JSON.parse(bearerToken).token;
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${bearerToken}`)
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');

    return this.http.post(
      `${constants.uriParts.exportDownloadV2}=${fileFormat}`,
      dataRequest,
      { headers, responseType: 'blob' }
    );
  }

  public loadExportFile(dataRequest: ExportRequest, fileFormat: string) {
    let bearerToken = localStorage.currentUser;
    bearerToken = JSON.parse(bearerToken).token;
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${bearerToken}`)
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');

    return this.http.post(
      `${constants.uriParts.exportV2}=${fileFormat}`,
      dataRequest,
      { headers, responseType: 'blob' }
    );
  }

  public loadCustomDashboards() {
    return this.http.get<DashboardFilter[]>(
      constants.uriParts.customDashboards
    );
  }

  public saveCustomDashboard(dashboard: DashboardFilter) {
    return this.http.post(constants.uriParts.customDashboards, dashboard);
  }

  public deleteCustomDashboard(id: number): Observable<Object> {
    return this.http.delete(
      constants.uriParts.customDashboards + '/' + id.toString()
    );
  }

  public updateCustomDashboard(dashboard: DashboardFilter): Observable<Object> {
    return this.http.put(
      constants.uriParts.customDashboards + '/' + dashboard.id?.toString(),
      dashboard
    );
  }

  getContinents() {
    return this.http.get<CityRegion[]>(constants.uriParts.continents);
  }

  public getCountries(continent: CityRegion): Observable<SelectorCountry[]> {
    if (this.cityCountries.has(continent.code)) {
      continent.disabled = false;
      return of(this.cityCountries.get(continent.code) || []);
    }
    return this.http
      .get<SelectorCountry[]>(constants.uriParts.countries(continent.code))
      .pipe(
        tap((countries) => {
          this.cityCountries.set(continent.code, countries);
        }),
        finalize(() => (continent.disabled = false))
      );
  }

  public getCities(continentCode: string, countryCode: string) {
    return this.http.get<SelectorCity[]>(
      constants.uriParts.cities(continentCode, countryCode)
    );
  }

  public getCitiesMapList(continentCode: string, countryCode: string) {
    return this.http.get<SelectorCity[]>(
      constants.uriParts.citiesMapList(continentCode, countryCode)
    );
  }

  public searchCities(query: string) {
    query = query
      .split(' ')
      .map(
        (queryWord) => queryWord.charAt(0).toUpperCase() + queryWord.slice(1)
      )
      .join(' ');
    return this.http.get<CitySearch[]>(constants.uriParts.citiesSearch, {
      params: { query },
    });
  }
}
